import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Hero } from '../../components/Hero/Hero';
import { Features } from '../../components/Features/Features';
import { HowItWorks } from '../../components/HowItWorks/HowItWorks';
import { Benefits } from '../../components/Benefits/Benefits';
import { FoundersClub } from '../../components/FoundersClub/FoundersClub';
import { PromoVideo } from '../../components/Promos/PromoVideo';
import { FeaturedAds } from '../../components/FeaturedAds/FeaturedAds';
import AnimatedBanner from '../../components/Promos/AnimatedBanner';
import PromoBanner from '../../components/Promos/PromoBanner';
import { useAuth } from '../../contexts/AuthContext';
import './HomePage.css';

const HomePage = () => {
  const { currentUser } = useAuth();

  return (
    <>
      <Helmet>
        <title>Qinto | India's Smartest AI Marketplace to Buy & Sell Used Items</title>
        <meta name="description" content="Sell old mobiles, bikes, and furniture instantly on Qinto. AI writes your ad from a photo. No spam calls, no fees, 100% secure." />
        <meta name="keywords" content="sell old mobile online, second hand bikes india, used furniture marketplace, Qinto app, AI classifieds, buy sell used items Hyderabad" />

        <meta property="og:title" content="Qinto - Buy & Sell Smarter. Absolutely Free." />
        <meta property="og:description" content="AI-powered marketplace for used items in India. List in 30 seconds." />
        <meta property="og:image" content="https://qinto.in/og-banner.png" />
        <meta property="og:url" content="https://qinto.in" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://qinto.in" />

        <script type="application/ld+json">{`
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How does the AI listing work on Qinto?",
        "acceptedAnswer": { "@type": "Answer", "text": "Our AI analyzes your photo, identifies the item, and automatically writes a title and description. You don't have to type a single word to start selling." }
      },
      {
        "@type": "Question",
        "name": "Is Qinto free to use?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes, 100% free. No listing fees, no commissions, no hidden charges." }
      },
      {
        "@type": "Question",
        "name": "Can I sell my bike or car on Qinto?",
        "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. Qinto supports electronics, vehicles, furniture, fashion, real estate and more - all for free." }
      }
    ]
  }
  `}</script>

        <script type="application/ld+json">{`
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Qinto",
    "url": "https://qinto.in",
    "logo": "https://qinto.in/logos/qinto-logo.png",
    "description": "AI-powered buy and sell marketplace for used items in India.",
    "foundingLocation": { "@type": "Place", "name": "Hyderabad, India" },
    "sameAs": ["https://play.google.com/store/apps/details?id=com.qinto.qintoapp"]
  }
  `}</script>
      </Helmet>

      <Hero />

      <main className="home-page-main">
        <div className="home-section home-featured-deals">
          <FeaturedAds />
        </div>

        {!currentUser && (
          <div className="home-section home-promo-strip">
            <AnimatedBanner />
          </div>
        )}

        {!currentUser && (
          <>
            <div className="home-section home-how-it-works">
              <HowItWorks />
            </div>

            <div className="home-section home-demo">
              <PromoVideo />
            </div>

            <div className="home-section home-features">
              <Features />
            </div>

            <div className="home-section home-benefits">
              <Benefits />
            </div>
          </>
        )}

        <section className="home-section faq-section">
          <div className="container">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <div className="faq-container">
              <div className="faq-item">
                <h3>How does the AI listing work?</h3>
                <p>Our AI analyzes your photo, identifies the item, and automatically writes a title and description. You don't have to type a single word to start selling.</p>
              </div>
              <div className="faq-item">
                <h3>Is it safe to meet buyers from Qinto?</h3>
                <p>Yes, but always follow our safety guidelines: meet in public places like malls or cafes and never share your banking OTP with anyone.</p>
              </div>
              <div className="faq-item">
                <h3>Can I sell my old car or bike on Qinto?</h3>
                <p>Absolutely. Qinto supports everything from electronics and fashion to automobiles and real estate. All for free.</p>
              </div>
            </div>
          </div>
          <div className="faq-action">
            <Link to="/faq" className="btn btn-primary">
              View All FAQs
            </Link>
          </div>
        </section>

        {!currentUser && (
          <div className="home-section home-founders">
            <FoundersClub />
          </div>
        )}

        <PromoBanner />

        <div className="home-section about-banner-full">
          <Link to="/about" className="about-banner-content" onClick={() => window.scrollTo(0, 0)}>
            <span className="about-text">
              Learn more about the <strong>Qinto Mission</strong>
            </span>
            <span className="about-arrow-circle">-&gt;</span>
          </Link>
        </div>
      </main>
    </>
  );
};

export default HomePage;
